import React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

interface PrintifyOrderLineItem {
  id?: string;
  title?: string;
  quantity?: number;
}

interface PrintifyOrderSummary {
  id: string;
  status: string;
  total_price: number;
  line_items?: PrintifyOrderLineItem[];
}

interface CheckoutSuccessLocationState {
  orders?: PrintifyOrderSummary[];
  email?: string;
}

const pageContainerClass = 'min-h-screen pt-24 md:pt-28 pb-12 bg-[var(--background)] transition-colors duration-300';
const surfaceClass = 'bg-[var(--surface)] text-[var(--text-primary)] border border-border rounded-islamic shadow-md';
const subtleTextClass = 'text-[var(--text-secondary)]';

const CheckoutSuccess: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const state = (location.state as CheckoutSuccessLocationState | undefined) ?? {};
  const { orders, email } = state;

  if (!orders || orders.length === 0) {
    return (
      <div className={pageContainerClass}>
        <div className="container-custom flex items-center justify-center min-h-[60vh]">
          <div className={`${surfaceClass} max-w-lg w-full p-10 text-center space-y-6`}>
            <h2 className="text-2xl font-bold text-[var(--text-primary)]">Invalid Page</h2>
            <p className={subtleTextClass}>
              We couldn't find any order information for this page. Please return to the store to continue shopping.
            </p>
            <button
              type="button"
              onClick={() => navigate('/')}
              className="btn-primary px-6 py-3"
            >
              Go Home
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={pageContainerClass}>
      <div className="container-custom max-w-4xl mx-auto space-y-10">
        <div className={`${surfaceClass} text-center p-10 space-y-6`}>
          <div className="w-20 h-20 bg-green-100 dark:bg-green-900/30 rounded-full flex items-center justify-center mx-auto">
            <svg
              className="w-12 h-12 text-green-600 dark:text-green-300"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M5 13l4 4L19 7"
              />
            </svg>
          </div>
          <div className="space-y-2">
            <h1 className="text-3xl font-bold text-[var(--text-primary)]">Order Placed Successfully!</h1>
            <p className={subtleTextClass}>Thank you for your purchase from TafsirArt.</p>
          </div>
        </div>

        <div className={`${surfaceClass} p-10 space-y-6`}>
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
            <h2 className="text-xl font-semibold text-[var(--text-primary)]">Order Details</h2>
            {email && (
              <p className={`${subtleTextClass} text-sm`}>
                Confirmation sent to <span className="font-medium text-[var(--text-primary)]">{email}</span>
              </p>
            )}
          </div>

          <div className="space-y-6">
            {orders.map(order => (
              <div key={order.id} className="border border-border rounded-islamic p-6 space-y-4">
                <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
                  <div className="space-y-1">
                    <p className="font-semibold text-[var(--text-primary)]">Order ID: {order.id}</p>
                    <p className={subtleTextClass}>
                      Status: <span className="font-medium text-[var(--text-primary)]">{order.status}</span>
                    </p>
                  </div>
                  <span className="text-2xl font-bold text-[var(--accent)]">
                    ${(order.total_price / 100).toFixed(2)}
                  </span>
                </div>
                <p className={`${subtleTextClass} text-sm`}>
                  {order.line_items?.length ?? 0} item{(order.line_items?.length ?? 0) === 1 ? '' : 's'} in this order
                </p>
              </div>
            ))}
          </div>
        </div>

        <div className="grid gap-6 md:grid-cols-2">
          <div className={`${surfaceClass} p-8 space-y-3 bg-blue-50/70 dark:bg-blue-900/20 border-blue-200/70 dark:border-blue-700/40`}>
            <h3 className="font-semibold text-blue-900 dark:text-blue-100 flex items-center gap-2">
              <span role="img" aria-label="email">📧</span>
              Check Your Email
            </h3>
            <p className="text-sm text-blue-800 dark:text-blue-100/80">
              We've sent a confirmation email with order details and tracking information. You'll receive updates as your
              order is processed and shipped.
            </p>
          </div>
          <div className={`${surfaceClass} p-8 space-y-4`}>
            <h3 className="font-semibold text-lg text-[var(--text-primary)]">What happens next?</h3>
            <ul className={`space-y-3 text-sm ${subtleTextClass}`}>
              <li className="flex items-start gap-3">
                <span className="text-[var(--primary)] font-bold">1.</span>
                <span>Your order will be processed by our print partner (Printify).</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="text-[var(--primary)] font-bold">2.</span>
                <span>You'll receive an email when your items are printed and shipped.</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="text-[var(--primary)] font-bold">3.</span>
                <span>Track your shipment using the tracking number provided.</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="text-[var(--primary)] font-bold">4.</span>
                <span>Enjoy your beautiful Islamic calligraphy art!</span>
              </li>
            </ul>
          </div>
        </div>

        <div className={`${surfaceClass} p-8 space-y-4 text-center`}>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button
              type="button"
              onClick={() => navigate('/')}
              className="btn-secondary px-8 py-3"
            >
              Back to Home
            </button>
            <button
              type="button"
              onClick={() => navigate('/store')}
              className="btn-primary px-8 py-3"
            >
              Continue Shopping
            </button>
          </div>

          <p className={`${subtleTextClass} text-sm`}>
            Need help with your order? Contact us at{' '}
            <a href="mailto:tafsirart@gmail.com" className="text-[var(--primary)] hover:text-[var(--accent)] font-medium">
              tafsirart@gmail.com
            </a>
          </p>
        </div>
      </div>
    </div>
  );
};

export default CheckoutSuccess;
